import { MdError } from '@react-icons/all-files/md/MdError'
import { MdSecurity } from '@react-icons/all-files/md/MdSecurity'
import { MdWarning } from '@react-icons/all-files/md/MdWarning'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '~/store'
import { addDirHandle, removeDirHandle, selectAllHandlesWithHashes } from '~/store/dirHandleSlice'
import { FileHandleStore, StoredHandle } from '~/utils/FileHandleStore'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'

const MultiLocationComponent = () => {
  const [storedHandles, setStoredHandles] = useState<StoredHandle[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [processingAction, setProcessingAction] = useState<boolean>(false)

  const handleIds = useSelector((state: RootState) => selectAllHandlesWithHashes(state))
  const fileHandleStore = new FileHandleStore()

  useEffect(() => {
    const loadHandles = async () => {
      try {
        await fileHandleStore.init()
        const loadedHandles = await Promise.all(
          handleIds.map(async h => {
            const handle = await fileHandleStore.getHandleById(h.id)
            return handle
          })
        )
        // Filter out any null values and set the stored handles
        setStoredHandles(loadedHandles.filter((h): h is StoredHandle => h !== null))
      } catch (error) {
        console.error('Error loading handles:', error)
        setErrorMessage('Failed to load repositories. Please try again.')
        BetterToast.error('Failed to load repositories')
      }
    }

    loadHandles()
  }, [handleIds])

  const handleSelectRepository = async () => {
    try {
      setProcessingAction(true)
      setErrorMessage(null)

      const dirHandle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      })

      await addDirHandle(dirHandle)
      BetterToast.success('Repository added successfully')
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error accessing file system:', error)
        // Show the actual error message if available
        const errorMessage = error.message || 'Failed to access the file system. Please try again.'
        setErrorMessage(errorMessage)
        BetterToast.error(errorMessage)
      }
    } finally {
      setProcessingAction(false)
    }
  }

  const handleRemoveLocation = async (id: string) => {
    try {
      setErrorMessage(null)
      await removeDirHandle(id)
      BetterToast.success('Repository removed successfully')
    } catch (error) {
      console.error('Failed to remove directory handle:', error)
      setErrorMessage('Failed to remove repository. Please try again.')
      BetterToast.error('Failed to remove repository')
    }
  }

  const formatCommitHash = (hash?: string) => {
    if (!hash) return 'None'
    return hash.slice(0, 7)
  }

  const getSecurityStatusIcon = (securityStatus?: string) => {
    switch (securityStatus) {
      case 'THREATS_FOUND':
        return <MdWarning className='text-red-600 mr-1' title='Security threat detected' />
      case 'SCANNING':
        return <MdSecurity className='text-blue-500 mr-1' title='Security scan in progress' />
      case 'CLEAN':
        return null
      default:
        return null
    }
  }

  const getNameStyle = (securityStatus?: string) => {
    switch (securityStatus) {
      case 'THREATS_FOUND':
        return 'text-red-600'
      case 'SCANNING':
        return 'text-blue-500'
      case 'CLEAN':
      default:
        return 'text-black/90'
    }
  }

  const getSecurityStatusText = (securityStatus?: string) => {
    switch (securityStatus) {
      case 'THREATS_FOUND':
        return 'security threat detected, sync stopped'
      case 'SCANNING':
        return 'scanning...'
      default:
        return null
    }
  }

  return (
    <div className='w-[396px] p-2 bg-white rounded-xl flex flex-col'>
      {/* Location header */}
      <div className='h-8 px-3 rounded-lg flex items-center'>
        <div className="flex-1 text-black/90 text-sm font-semibold leading-[22px] font-['Geist']">
          Repositories
        </div>
      </div>

      {/* Content area */}
      <div className='flex-1 px-2 pb-2 rounded-xl flex flex-col gap-2'>
        {/* Repository locations */}
        {storedHandles.map(storedHandle => {
          const handleInfo = handleIds.find(h => h.id === storedHandle.id)
          const securityStatusText = getSecurityStatusText(handleInfo?.securityStatus)

          return (
            <div key={storedHandle.id} className='flex items-center gap-2'>
              <div className='flex-1 h-8 px-1 bg-white rounded-md overflow-hidden flex items-center'>
                <div className='flex-1 h-8 py-1 flex items-center'>
                  <div
                    className={cm(
                      "flex items-center flex-1 h-[22px] text-sm font-normal leading-[22px] truncate font-['Geist']",
                      getNameStyle(handleInfo?.securityStatus)
                    )}
                  >
                    {getSecurityStatusIcon(handleInfo?.securityStatus)}
                    {storedHandle.handle.name}
                    {handleInfo?.securityStatus === 'CLEAN' && (
                      <span className="text-black/60 font-['Geist Mono'] ml-1">
                        (id: {storedHandle.id.slice(0, 6)}; hash:{' '}
                        {formatCommitHash(handleInfo?.lastFolderHash)})
                      </span>
                    )}
                    {securityStatusText && (
                      <span
                        className={cm(
                          'text-xs ml-1',
                          handleInfo?.securityStatus === 'THREATS_FOUND'
                            ? 'text-red-600'
                            : handleInfo?.securityStatus === 'SCANNING'
                              ? 'text-blue-500'
                              : ''
                        )}
                      >
                        ({securityStatusText})
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleRemoveLocation(storedHandle.id)}
                className='h-8 w-8 flex items-center justify-center text-black/45 hover:text-black/60'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M7.03125 3.59375H6.875C6.96094 3.59375 7.03125 3.52344 7.03125 3.4375V3.59375H12.9688V3.4375C12.9688 3.52344 13.0391 3.59375 13.125 3.59375H12.9688V5H14.375V3.4375C14.375 2.74805 13.8145 2.1875 13.125 2.1875H6.875C6.18555 2.1875 5.625 2.74805 5.625 3.4375V5H7.03125V3.59375ZM16.875 5H3.125C2.7793 5 2.5 5.2793 2.5 5.625V6.25C2.5 6.33594 2.57031 6.40625 2.65625 6.40625H3.83594L4.31836 16.6211C4.34961 17.2871 4.90039 17.8125 5.56641 17.8125H14.4336C15.1016 17.8125 15.6504 17.2891 15.6816 16.6211L16.1641 6.40625H17.3438C17.4297 6.40625 17.5 6.33594 17.5 6.25V5.625C17.5 5.2793 17.2207 5 16.875 5ZM14.2832 16.4062H5.7168L5.24414 6.40625H14.7559L14.2832 16.4062Z'
                    fill='currentColor'
                  />
                </svg>
              </button>
            </div>
          )
        })}

        {/* Error message display */}
        {errorMessage && (
          <div className='flex items-center mt-1 text-sm text-red-600 mb-2'>
            <MdError className='mr-1' />
            {errorMessage}
          </div>
        )}

        {/* Add Location button */}
        <button
          onClick={handleSelectRepository}
          disabled={processingAction}
          className={cm(
            'w-full flex items-center gap-1 text-[#806BD4] rounded-md p-2',
            processingAction ? 'opacity-50 cursor-wait' : 'hover:bg-[#806BD4]/5 cursor-pointer'
          )}
        >
          {processingAction ? (
            'Processing...'
          ) : (
            <>
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M7.53125 2.375H8.46875C8.55208 2.375 8.59375 2.41667 8.59375 2.5V13.5C8.59375 13.5833 8.55208 13.625 8.46875 13.625H7.53125C7.44792 13.625 7.40625 13.5833 7.40625 13.5V2.5C7.40625 2.41667 7.44792 2.375 7.53125 2.375Z'
                  fill='currentColor'
                />
                <path
                  d='M2.75 7.40625H13.25C13.3333 7.40625 13.375 7.44792 13.375 7.53125V8.46875C13.375 8.55208 13.3333 8.59375 13.25 8.59375H2.75C2.66667 8.59375 2.625 8.55208 2.625 8.46875V7.53125C2.625 7.44792 2.66667 7.40625 2.75 7.40625Z'
                  fill='currentColor'
                />
              </svg>
              <span className="text-xs font-['Geist Mono'] font-extrabold leading-4">
                Add Repository
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default MultiLocationComponent
